import { useCallback, useEffect, useState } from 'react'
import { dumperHourmeterService } from '../services/dumperApi'
import { useAuth } from '../contexts/AuthContext'
import type { DumperHourmeter, DumperHourmetersListQuery } from '../types/dumper'

const CACHE_PREFIX = 'dumper-cache-'

function getCacheKey(operatorId: number | string, scope: string) {
  return `${CACHE_PREFIX}${scope}-${operatorId}`
}

export function useDumperHourmeters(query: DumperHourmetersListQuery = {}) {
  const { operator } = useAuth()
  const [trips, setTrips] = useState<DumperHourmeter[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const cacheKey = operator?.id != null
    ? getCacheKey(operator.id, JSON.stringify(query))
    : null

  // Hidrata desde cache primero
  useEffect(() => {
    if (!cacheKey) return
    try {
      const raw = localStorage.getItem(cacheKey)
      if (raw) {
        const parsed = JSON.parse(raw) as DumperHourmeter[]
        if (Array.isArray(parsed)) setTrips(parsed)
      }
    } catch {
      // noop
    }
  }, [cacheKey])

  const fetchTrips = useCallback(async () => {
    if (!operator) return
    setLoading(true)
    setError(null)
    try {
      const data = await dumperHourmeterService.list(query)
      setTrips(Array.isArray(data) ? data : [])
      if (cacheKey && Array.isArray(data)) {
        localStorage.setItem(cacheKey, JSON.stringify(data))
      }
    } catch (err: any) {
      console.error('Error fetching dumper hourmeters:', err)
      setError(err?.response?.data?.message || 'Error al cargar viajes de volquetas')
    } finally {
      setLoading(false)
    }
  }, [operator, JSON.stringify(query), cacheKey])

  useEffect(() => {
    fetchTrips()
  }, [fetchTrips])

  return { trips, loading, error, refetch: fetchTrips }
}
